import { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase-client';
import { ProductUpdateArticle } from '../../pages/help';
import { useOnlineStatus } from '../../utils/onlineStatus';

const CATEGORY_OPTIONS = [
  { value: 'new_feature', label: 'New feature' },
  { value: 'improvement', label: 'Improvement' },
  { value: 'fix', label: 'Fix' },
  { value: 'announcement', label: 'Announcement' },
];

function todayValue() {
  return new Date().toISOString().slice(0, 10);
}

function emptyForm() {
  return {
    id: '',
    status: 'draft',
    title: '',
    summary: '',
    releaseDate: todayValue(),
    sections: [{ heading: '', category: 'new_feature', itemsText: '' }],
    actionLabel: '',
    actionUrl: '',
    correctionReason: '',
  };
}

function updateToForm(update = {}) {
  return {
    id: update.id || '',
    status: update.status || 'draft',
    title: update.title || '',
    summary: update.summary || '',
    releaseDate: update.releaseDate || todayValue(),
    sections: (Array.isArray(update.sections) && update.sections.length
      ? update.sections
      : [{ heading: '', items: [] }]
    ).map((section) => ({
      heading: section.heading || '',
      category: section.category || 'announcement',
      itemsText: (Array.isArray(section.items) ? section.items : []).join('\n'),
    })),
    actionLabel: update.action?.label || '',
    actionUrl: update.action?.url || '',
    correctionReason: '',
  };
}

function formToPayload(form) {
  return {
    id: form.id || undefined,
    title: form.title,
    summary: form.summary,
    releaseDate: form.releaseDate,
    sections: form.sections.map((section) => ({
      heading: section.heading,
      category: section.category,
      items: String(section.itemsText || '')
        .split('\n')
        .map((item) => item.trim().replace(/^[-•]\s*/, ''))
        .filter(Boolean),
    })),
    actionLabel: form.actionLabel,
    actionUrl: form.actionUrl,
    correctionReason: form.correctionReason,
  };
}

function displayDate(value) {
  if (!value) return 'Not available';
  const date = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function statusLabel(status) {
  if (status === 'published') return 'Published';
  if (status === 'withdrawn') return 'Withdrawn';
  return 'Draft';
}

function sectionTypeSummary(update = {}) {
  const labels = Array.from(new Set(
    (Array.isArray(update.sections) ? update.sections : [])
      .map((section) => section.category)
      .map((category) => CATEGORY_OPTIONS.find((option) => option.value === category)?.label)
      .filter(Boolean)
  ));
  return labels.length ? labels.join(', ') : 'Announcement';
}

export default function ProductUpdatesPanel() {
  const online = useOnlineStatus();
  const [updates, setUpdates] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [notifyClubAdmins, setNotifyClubAdmins] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadUpdates = async () => {
    setLoading(true);
    setError('');
    try {
      const fn = httpsCallable(functions, 'listAdminProductUpdates');
      const response = await fn({});
      setUpdates(Array.isArray(response.data?.updates) ? response.data.updates : []);
    } catch (err) {
      setUpdates([]);
      setError(err?.message || 'Could not load product updates.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUpdates();
  }, []);

  const previewUpdate = useMemo(() => {
    const payload = formToPayload(form);
    return {
      id: form.id || 'preview',
      ...payload,
      action: payload.actionUrl && payload.actionLabel
        ? { url: payload.actionUrl, label: payload.actionLabel }
        : null,
      publishedAt: form.status === 'published' ? new Date() : null,
      updatedAt: form.status === 'published' ? new Date() : null,
    };
  }, [form]);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setMessage('');
  };

  const setSectionField = (index, field, value) => {
    setForm((current) => ({
      ...current,
      sections: current.sections.map((section, sectionIndex) => (
        sectionIndex === index ? { ...section, [field]: value } : section
      )),
    }));
  };

  const addSection = () => {
    setForm((current) => ({
      ...current,
      sections: [...current.sections, { heading: '', category: 'new_feature', itemsText: '' }],
    }));
  };

  const removeSection = (index) => {
    setForm((current) => ({
      ...current,
      sections: current.sections.filter((section, sectionIndex) => sectionIndex !== index),
    }));
  };

  const moveSection = (index, direction) => {
    setForm((current) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= current.sections.length) return current;
      const sections = [...current.sections];
      [sections[index], sections[targetIndex]] = [sections[targetIndex], sections[index]];
      return { ...current, sections };
    });
  };

  const beginNew = () => {
    setForm(emptyForm());
    setEditing(true);
    setPreviewing(false);
    setPublishing(false);
    setError('');
    setMessage('');
  };

  const beginEdit = (update) => {
    setForm(updateToForm(update));
    setEditing(true);
    setPreviewing(update.status === 'withdrawn');
    setPublishing(false);
    setError('');
    setMessage('');
  };

  const closeEditor = () => {
    setEditing(false);
    setPreviewing(false);
    setPublishing(false);
    setForm(emptyForm());
  };

  const save = async (openPublishReview = false) => {
    if (!online || busy) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const fn = httpsCallable(functions, 'saveProductUpdate');
      const response = await fn(formToPayload(form));
      const nextId = response.data?.id || form.id;
      setForm((current) => ({ ...current, id: nextId, status: response.data?.status || current.status }));
      setMessage(form.status === 'published' ? 'Correction saved.' : 'Draft saved.');
      await loadUpdates();
      if (openPublishReview && response.data?.status === 'draft') {
        setPreviewing(true);
        setPublishing(true);
      }
    } catch (err) {
      setError(err?.message || 'Could not save this product update.');
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!online || busy || !form.id) return;
    setBusy(true);
    setError('');
    try {
      const fn = httpsCallable(functions, 'publishProductUpdate');
      await fn({ id: form.id, notifyClubAdmins });
      setMessage(notifyClubAdmins
        ? 'Update published. Club-admin notifications are being delivered.'
        : 'Update published quietly.');
      setPublishing(false);
      setForm((current) => ({ ...current, status: 'published' }));
      await loadUpdates();
    } catch (err) {
      setError(err?.message || 'Could not publish this product update.');
    } finally {
      setBusy(false);
    }
  };

  const deleteDraft = async (update) => {
    if (!online || busy) return;
    if (!window.confirm(`Delete the draft “${update.title}”?`)) return;
    setBusy(true);
    setError('');
    try {
      const fn = httpsCallable(functions, 'deleteProductUpdateDraft');
      await fn({ id: update.id });
      if (form.id === update.id) closeEditor();
      await loadUpdates();
    } catch (err) {
      setError(err?.message || 'Could not delete this draft.');
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (update) => {
    if (!online || busy) return;
    const reason = window.prompt('Why is this published update being withdrawn?');
    if (!reason?.trim()) return;
    if (!window.confirm('Withdraw this update from the public What’s New history? The audit record will be retained.')) return;
    setBusy(true);
    setError('');
    try {
      const fn = httpsCallable(functions, 'withdrawProductUpdate');
      await fn({ id: update.id, reason: reason.trim() });
      if (form.id === update.id) closeEditor();
      await loadUpdates();
    } catch (err) {
      setError(err?.message || 'Could not withdraw this product update.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="admin-section admin-product-updates">
      <div className="admin-section__header">
        <div>
          <h2>Product Updates</h2>
          <p>Draft, preview, and publish the updates shown publicly on Help.</p>
        </div>
        <button type="button" className="button button-primary" onClick={beginNew} disabled={busy || !online}>
          New update
        </button>
      </div>

      <div className="admin-section__body admin-product-updates__body">
        {!online && <p className="admin-product-updates__notice">Reconnect to manage product updates.</p>}
        {error && <p className="text-danger admin-inline-error" role="alert">{error}</p>}
        {message && <p className="admin-product-updates__success" role="status">{message}</p>}

        <div className={editing ? 'admin-product-updates__layout admin-product-updates__layout--editing' : 'admin-product-updates__layout'}>
          <div className="admin-product-updates__list">
            <div className="admin-product-updates__list-header">
              <h3>Release history</h3>
              <button type="button" className="btn btn--secondary btn--xs" onClick={loadUpdates} disabled={loading}>
                Refresh
              </button>
            </div>
            {loading ? (
              <p className="text-muted">Loading product updates…</p>
            ) : updates.length === 0 ? (
              <div className="admin-product-updates__empty">
                <strong>No product updates yet</strong>
                <span>Create the first draft when you’re ready to share a release.</span>
              </div>
            ) : (
              updates.map((update) => (
                <article
                  key={update.id}
                  className={`admin-product-update-row ${form.id === update.id ? 'active' : ''}`}
                >
                  <button type="button" className="admin-product-update-row__main" onClick={() => beginEdit(update)}>
                    <span className={`admin-product-update-row__status admin-product-update-row__status--${update.status}`}>
                      {statusLabel(update.status)}
                    </span>
                    <strong>{update.title}</strong>
                    <span>{displayDate(update.releaseDate)} · {sectionTypeSummary(update)}</span>
                    {update.status === 'published' && update.notificationRequested && (
                      <span>
                        {update.deliveryStatus === 'complete'
                          ? `${update.deliveredCount} club admin notification${update.deliveredCount === 1 ? '' : 's'} delivered`
                          : 'Notification delivery pending'}
                      </span>
                    )}
                    {update.correctionCount > 0 && (
                      <span>{update.correctionCount} recorded correction{update.correctionCount === 1 ? '' : 's'}</span>
                    )}
                  </button>
                  <div className="admin-product-update-row__actions">
                    {update.status === 'draft' && (
                      <button type="button" className="btn btn--danger btn--xs" onClick={() => deleteDraft(update)} disabled={busy || !online}>
                        Delete draft
                      </button>
                    )}
                    {update.status === 'published' && (
                      <button type="button" className="btn btn--secondary btn--xs" onClick={() => withdraw(update)} disabled={busy || !online}>
                        Withdraw
                      </button>
                    )}
                  </div>
                </article>
              ))
            )}
          </div>

          {editing && (
            <div className="admin-product-update-editor">
              <div className="admin-product-update-editor__header">
                <div>
                  <span>{form.id ? statusLabel(form.status) : 'New draft'}</span>
                  <h3>{form.status === 'withdrawn' ? 'View withdrawn update' : form.id ? 'Edit product update' : 'Create product update'}</h3>
                </div>
                <button type="button" className="btn btn--secondary btn--xs" onClick={closeEditor}>Close</button>
              </div>

              {previewing ? (
                <div className="admin-product-update-preview">
                  <div className="admin-product-update-preview__bar">
                    <strong>Public preview</strong>
                    <button
                      type="button"
                      className="btn btn--secondary btn--xs"
                      onClick={() => {
                        if (form.status === 'withdrawn') closeEditor();
                        else {
                          setPreviewing(false);
                          setPublishing(false);
                        }
                      }}
                    >
                      {form.status === 'withdrawn' ? 'Close preview' : 'Back to editor'}
                    </button>
                  </div>
                  <ProductUpdateArticle update={previewUpdate} featured />
                </div>
              ) : (
                <form className="admin-product-update-form" onSubmit={(event) => { event.preventDefault(); save(false); }}>
                  <label>
                    <span>Title</span>
                    <input value={form.title} maxLength={120} onChange={(event) => setField('title', event.target.value)} required />
                  </label>
                  <label>
                    <span>Short summary</span>
                    <textarea value={form.summary} maxLength={320} rows={3} onChange={(event) => setField('summary', event.target.value)} required />
                    <small>{form.summary.length}/320</small>
                  </label>
                  <label>
                    <span>Release date</span>
                    <input type="date" value={form.releaseDate} onChange={(event) => setField('releaseDate', event.target.value)} required />
                  </label>

                  <fieldset className="admin-product-update-sections">
                    <legend>Update sections</legend>
                    {form.sections.map((section, index) => (
                      <div key={index} className="admin-product-update-section">
                        <div className="admin-product-update-section__header">
                          <strong>Section {index + 1}</strong>
                          <div className="admin-product-update-section__actions">
                            <button
                              type="button"
                              className="btn btn--secondary btn--xs"
                              onClick={() => moveSection(index, -1)}
                              disabled={index === 0}
                              aria-label={`Move section ${index + 1} up`}
                            >
                              Move up
                            </button>
                            <button
                              type="button"
                              className="btn btn--secondary btn--xs"
                              onClick={() => moveSection(index, 1)}
                              disabled={index === form.sections.length - 1}
                              aria-label={`Move section ${index + 1} down`}
                            >
                              Move down
                            </button>
                            {form.sections.length > 1 && (
                              <button type="button" className="btn btn--secondary btn--xs" onClick={() => removeSection(index)}>
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="admin-product-update-form__row">
                          <label>
                            <span>Section type</span>
                            <select value={section.category} onChange={(event) => setSectionField(index, 'category', event.target.value)}>
                              {CATEGORY_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </label>
                          <label>
                            <span>Heading</span>
                            <input value={section.heading} maxLength={100} onChange={(event) => setSectionField(index, 'heading', event.target.value)} required />
                          </label>
                        </div>
                        <label>
                          <span>Bullet points</span>
                          <textarea
                            value={section.itemsText}
                            rows={4}
                            placeholder={'One point per line\nA second helpful detail'}
                            onChange={(event) => setSectionField(index, 'itemsText', event.target.value)}
                            required
                          />
                        </label>
                      </div>
                    ))}
                    <button type="button" className="btn btn--secondary btn--sm" onClick={addSection} disabled={form.sections.length >= 12}>
                      Add section
                    </button>
                  </fieldset>

                  <fieldset className="admin-product-update-action">
                    <legend>Optional action</legend>
                    <div className="admin-product-update-form__row">
                      <label>
                        <span>Button label</span>
                        <input value={form.actionLabel} maxLength={60} onChange={(event) => setField('actionLabel', event.target.value)} placeholder="View the feature" />
                      </label>
                      <label>
                        <span>Member Nest path</span>
                        <input value={form.actionUrl} maxLength={500} onChange={(event) => setField('actionUrl', event.target.value)} placeholder="/teams" />
                      </label>
                    </div>
                  </fieldset>

                  {form.status === 'published' && (
                    <label>
                      <span>Correction reason</span>
                      <textarea
                        value={form.correctionReason}
                        maxLength={300}
                        rows={2}
                        onChange={(event) => setField('correctionReason', event.target.value)}
                        placeholder="Recorded internally with the previous version"
                        required
                      />
                    </label>
                  )}

                  <div className="admin-product-update-form__actions">
                    <button type="button" className="btn btn--secondary" onClick={() => setPreviewing(true)}>
                      Preview
                    </button>
                    <button type="submit" className="button button-primary" disabled={busy || !online}>
                      {busy ? 'Saving…' : form.status === 'published' ? 'Save correction' : 'Save draft'}
                    </button>
                    {form.id && form.status === 'draft' && (
                      <button type="button" className="button" onClick={() => save(true)} disabled={busy || !online}>
                        Review and publish
                      </button>
                    )}
                  </div>
                </form>
              )}

              {publishing && previewing && (
                <div className="admin-product-update-publish" role="dialog" aria-modal="true" aria-label="Publish product update">
                  <div>
                    <h3>Publish this update?</h3>
                    <p>It will become visible immediately on the public Help page.</p>
                    <label>
                      <input
                        type="checkbox"
                        checked={notifyClubAdmins}
                        onChange={(event) => setNotifyClubAdmins(event.target.checked)}
                      />
                      <span>
                        <strong>Notify club admins</strong>
                        <small>Create one notification for each current club admin.</small>
                      </span>
                    </label>
                    <div>
                      <button type="button" className="btn btn--secondary" onClick={() => setPublishing(false)} disabled={busy}>
                        Keep reviewing
                      </button>
                      <button type="button" className="button button-primary" onClick={publish} disabled={busy || !online}>
                        {busy ? 'Publishing…' : 'Publish now'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
